import { useEffect, useMemo, useRef, useState } from "react";
import { computeHtmlSrcDocFingerprint } from "../../../lib/htmlIframeSrcDoc";
import { getPresignedUrlExpiresAtMs } from "../../../lib/presignedUrl";

export interface HtmlCardDisplayDoc {
  /** React key for the iframe — changing it is the ONLY thing that remounts. */
  mountKey: string;
  srcDoc: string;
}

interface PendingDoc {
  fingerprint: string;
  srcDoc: string;
}

export interface UseHtmlCardSrcDocParams {
  /** Presigned URL of the HTML body. Rotation alone never refetches — the
   *  fetch is keyed on the stable S3 path + `updated_at` + URL expiry. */
  url: string | null | undefined;
  updated_at?: string;
  /** Optional async rewrite applied to the fetched HTML before preparing —
   *  used to swap embedded public file URLs for presigned ones. */
  transformHtml?: (html: string) => Promise<string> | string;
  /**
   * Render-option identity folded into the fingerprint (theme, transparency).
   * A salt change forces a fresh document; an unchanged salt + unchanged body
   * is a guaranteed no-op.
   */
  optionsSalt: string;
  /** Turn the transformed body into a srcDoc string (prepareSrcDocHtml with
   *  the card's options). Must be memoized on the same identity the salt
   *  captures — its identity changing re-runs the delivery effect. */
  prepare: (transformed: string) => string;
  /** While true, refetched content is stashed instead of committed so the
   *  iframe never remounts mid edit-session; the stash is reconciled when
   *  this flips back to false. */
  contentFrozen?: boolean;
}

export interface UseHtmlCardSrcDocResult {
  displayDoc: HtmlCardDisplayDoc | null;
}

/**
 * Shared fetch → transform → fingerprint → prepare pipeline for the canvas
 * HTML cards (block and legacy thumb variants).
 *
 * Remount discipline: the fingerprint hashes the ORIGINAL (pre-transform)
 * body + `optionsSalt`, and an unchanged fingerprint performs ZERO state
 * updates — not "sets the same value". Rewriting the `srcdoc` attribute
 * reloads an iframe even when the React key is unchanged, so the equality
 * check must return before `setDisplayDoc` is ever called. Presigned URL
 * rotation therefore never restarts in-frame JS.
 *
 * Edit sessions hold `contentFrozen` while typing, saving, and for a short
 * settle window afterwards, so every refetch a save triggers lands in the
 * stash rather than reloading the frame under the user. On unfreeze the stash
 * is applied once — the single, content-identical remount that ends a
 * session. (A local region-merge could avoid even that one; it is deliberately
 * deferred, since it costs a byte-exact port of the backend's substitution
 * algorithm to save one reload of content the user is already looking at.)
 */
export function useHtmlCardSrcDoc({
  url,
  updated_at,
  transformHtml,
  optionsSalt,
  prepare,
  contentFrozen = false,
}: UseHtmlCardSrcDocParams): UseHtmlCardSrcDocResult {
  const [displayDoc, setDisplayDoc] = useState<HtmlCardDisplayDoc | null>(null);

  const expectedFingerprintRef = useRef<string | null>(null);
  const pendingDocRef = useRef<PendingDoc | null>(null);

  const s3Path = useMemo(() => {
    if (!url) return null;
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }, [url]);

  const urlRef = useRef(url);
  useEffect(() => {
    urlRef.current = url;
  }, [url]);

  const transformHtmlRef = useRef(transformHtml);
  useEffect(() => {
    transformHtmlRef.current = transformHtml;
  }, [transformHtml]);

  const urlExpiresAtMs = useMemo(
    () => getPresignedUrlExpiresAtMs(url ?? undefined),
    [url],
  );

  const contentFrozenRef = useRef(contentFrozen);
  // Whether a document has ever been committed to the card (see the freeze
  // branch below).
  const hasDeliveredRef = useRef(false);

  useEffect(() => {
    if (!s3Path || !urlRef.current) return;
    let cancelled = false;

    void fetch(urlRef.current)
      .then((res) => (res.ok ? res.text() : undefined))
      .then(async (original) => {
        if (cancelled || original == null) return;
        const fingerprint =
          computeHtmlSrcDocFingerprint(original, undefined) + optionsSalt;
        // Same body + same options: zero state updates.
        if (fingerprint === expectedFingerprintRef.current) return;

        const transform = transformHtmlRef.current;
        const transformed = transform ? await transform(original) : original;
        if (cancelled || transformed == null) return;
        const srcDoc = prepare(transformed);

        // Freezing protects a MOUNTED document from remounting under the
        // user; before anything has been delivered there is nothing to
        // protect, and stashing the first document leaves the card blank
        // forever (a lightbox that opens straight into an edit session
        // mounts frozen).
        if (contentFrozenRef.current && hasDeliveredRef.current) {
          // Latest wins: only the newest server state is worth applying once
          // the session ends.
          pendingDocRef.current = { fingerprint, srcDoc };
          return;
        }
        expectedFingerprintRef.current = fingerprint;
        pendingDocRef.current = null;
        hasDeliveredRef.current = true;
        setDisplayDoc({ mountKey: fingerprint, srcDoc });
      })
      .catch(() => {
        // Keep existing content on error — don't go blank.
      });

    return () => {
      cancelled = true;
    };
  }, [s3Path, updated_at, urlExpiresAtMs, optionsSalt, prepare]);

  // Unfreeze reconcile: apply a stashed refetch only if it still differs from
  // what the card already shows.
  useEffect(() => {
    contentFrozenRef.current = contentFrozen;
    if (contentFrozen) return;
    const pending = pendingDocRef.current;
    if (!pending) return;
    pendingDocRef.current = null;
    if (pending.fingerprint === expectedFingerprintRef.current) return;
    expectedFingerprintRef.current = pending.fingerprint;
    setDisplayDoc({ mountKey: pending.fingerprint, srcDoc: pending.srcDoc });
  }, [contentFrozen]);

  return { displayDoc };
}
